import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePresence } from '../contexts/PresenceContext';
import { useToast } from '../contexts/ToastContext';

const ROOM_STORAGE_KEY = 'current_room_state';

interface RoomState {
  roomCode: string;
  userId: string;
  timestamp: number;
  wasHost: boolean;
}

export const useRoomReconnection = () => {
  const { user } = useAuth();
  const { setPresence } = usePresence();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Save current room state to localStorage
  const saveRoomState = useCallback((roomCode: string, isHost?: boolean) => {
    if (!user?.uid) return;
    
    const roomState: RoomState = {
      roomCode,
      userId: user.uid,
      timestamp: Date.now(),
      wasHost: isHost || false
    };
    
    localStorage.setItem(ROOM_STORAGE_KEY, JSON.stringify(roomState));
    console.log('💾 Room state saved:', roomState);
  }, [user]);

  // Clear room state from localStorage
  const clearRoomState = useCallback(() => {
    localStorage.removeItem(ROOM_STORAGE_KEY);
    console.log('🗑️ Room state cleared');
  }, []);

  // Get saved room state
  const getSavedRoomState = useCallback((): RoomState | null => {
    try {
      const saved = localStorage.getItem(ROOM_STORAGE_KEY);
      if (!saved) return null;
      
      const state: RoomState = JSON.parse(saved);
      
      // Check if state is not too old (1 hour max)
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - state.timestamp > oneHour) {
        console.log('⏰ Room state expired, clearing...');
        clearRoomState();
        return null;
      }
      
      return state;
    } catch (error) {
      console.error('Error getting saved room state:', error);
      clearRoomState();
      return null;
    }
  }, [clearRoomState]);

  // Attempt to reconnect to saved room
  const attemptReconnection = useCallback(async () => {
    if (!user?.uid) return;
    
    // Check if we intentionally cleared state (don't reconnect in this case)
    const intentionalClear = sessionStorage.getItem('intentional_clear');
    if (intentionalClear === 'true') {
      console.log('🚫 RECONNECTION: Skipping reconnection due to intentional clear');
      sessionStorage.removeItem('intentional_clear'); // Clear the flag
      return;
    }
    
    const savedState = getSavedRoomState();
    if (!savedState) return;
    
    // Don't reconnect if user ID doesn't match
    if (savedState.userId !== user.uid) {
      console.log('👤 User ID mismatch, clearing state');
      clearRoomState();
      return;
    }

    // Don't reconnect if already in a room route
    if (location.pathname.includes('/lobby/') || 
        location.pathname.includes('/game/') ||
        location.pathname.includes('/results/') ||
        location.pathname.includes('/summary/')) {
      console.log('🏠 Already in room route, skipping reconnection');
      return;
    }

    console.log('🔄 Attempting reconnection to room:', savedState.roomCode);

    try {
      // Check if room still exists and user is still a member
      const roomRef = doc(db, 'rooms', savedState.roomCode);
      const roomSnap = await getDoc(roomRef);
      
      if (!roomSnap.exists()) {
        console.log('🚫 Room no longer exists');
        clearRoomState();
        showError('La sala anterior ya no existe');
        return;
      }

      const roomData = roomSnap.data();
      
      // Check if user is still a player in the room
      const playerRef = doc(db, 'rooms', savedState.roomCode, 'players', user.uid);
      const playerSnap = await getDoc(playerRef);
      
      if (!playerSnap.exists()) {
        console.log('🚫 User no longer in room');
        clearRoomState();
        showError('Ya no eres parte de la sala anterior');
        return;
      }

      // Reconnect to presence system
      await setPresence(savedState.roomCode);
      
      // Navigate to appropriate room route based on game status
      const gameStatus = roomData.status;
      const inGameStatuses = [
        'QUESTION',
        'REVEAL',
        'WRITING_CHALLENGES',
        'CHALLENGE_REVEAL',
        'IMPOSTOR_PLAYING',
        'IMPOSTOR_FIRST_REVEAL',
        'IMPOSTOR_SECOND_VOTE',
        'IMPOSTOR_REVEAL'
      ];
      let targetRoute = `/lobby/${savedState.roomCode}`;

      if (inGameStatuses.includes(gameStatus)) {
        targetRoute = `/game/${savedState.roomCode}`;
      } else if (gameStatus === 'ENDED') {
        targetRoute = `/summary/${savedState.roomCode}`;
      } else if (gameStatus === 'ARCHIVED') {
        // Room was closed; don't reconnect into it.
        clearRoomState();
        showError('La sala anterior ya no está disponible');
        return;
      }
      
      console.log('✅ Reconnection successful, navigating to:', targetRoute);
      navigate(targetRoute);
      showSuccess(`Reconectado a la sala ${savedState.roomCode}`);
      
    } catch (error: any) {
      console.error('❌ Reconnection failed:', error);
      clearRoomState();
      showError('No se pudo reconectar a la sala anterior');
    }
  }, [user, getSavedRoomState, clearRoomState, location.pathname, setPresence, navigate, showError, showSuccess]);

  // Auto-reconnect on app load
  useEffect(() => {
    if (user?.uid) {
      // Small delay to ensure all contexts are ready
      const timer = setTimeout(attemptReconnection, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, attemptReconnection]);

  return {
    saveRoomState,
    clearRoomState,
    getSavedRoomState,
    attemptReconnection
  };
};